/**
 * Pattern Component Caching Helper (Database-Backed)
 * 
 * Replaces sessionStorage with MySQL user_pattern_cache table.
 * Benefits:
 * - ✅ Persists across sessions and browser tabs
 * - ✅ Automatic parameter change detection (input_hash)
 * - ✅ Server-side TTL validation and cleanup
 * - ✅ User-scoped cache isolation
 * 
 * Usage:
 *   const cached = await getCachedPatternData('pattern_concentration', 30);
 *   if (cached) { setData(cached); return; }
 *   const data = await fetchFromAPI(...);
 *   await setCachedPatternData('pattern_concentration', 30, data);
 */

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Get cached pattern data from database
 * 
 * @param {string} patternId - Pattern identifier (e.g., "pattern_concentration")
 * @param {number} lookbackDays - Lookback window in days
 * @param {Object} additionalParams - Optional parameters affecting cache validity
 * @returns {Promise<Object|null>} - Cached data object or null if invalid/expired
 * 
 * @example
 * const data = await getCachedPatternData('pattern_concentration', 30);
 * if (data) console.log('From cache:', data);
 */
export const getCachedPatternData = async (patternId, lookbackDays, additionalParams = {}) => {
    try {
        // Normalize: "pattern_38" -> 38
        let cleanId = patternId;
        if (typeof patternId === 'string' && patternId.startsWith('pattern_')) {
            cleanId = parseInt(patternId.replace('pattern_', ''), 10);
        }

        const response = await fetch(`${API_BASE_URL}/api/cache/pattern-get`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                pattern_id: cleanId,
                lookback_days: lookbackDays,
                additional_params: additionalParams,
            })
        });
        if (!response.ok) return null;
        const result = await response.json();
        return result.data; // Returns null if expired or missing
    } catch (err) {
        return null;
    }
};

/**
 * Store pattern data in database cache
 * 
 * @param {string} patternId - Pattern identifier
 * @param {number} lookbackDays - Lookback window in days
 * @param {Object} patternData - API response data to cache
 * @param {Object} additionalParams - Optional parameters affecting cache validity
 * @returns {Promise<boolean>} - True if stored successfully
 * 
 * @example
 * const data = await fetchPatternAPI(...);
 * await setCachedPatternData('pattern_concentration', 30, data);
 */
export const setCachedPatternData = async (patternId, lookbackDays, patternData, additionalParams = {}) => {
    try {
        // PRE-NORMALIZATION: Extract number if string "pattern_38" is passed
        let cleanId = patternId;
        if (typeof patternId === 'string' && patternId.startsWith('pattern_')) {
            const num = patternId.replace('pattern_', '');
            if (!isNaN(num)) cleanId = parseInt(num, 10);
        }

        const response = await fetch(
            `${API_BASE_URL}/api/cache/pattern-set`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    pattern_id: cleanId, // Sent as pure number
                    lookback_days: lookbackDays,
                    pattern_data: patternData,
                    additional_params: additionalParams,
                })
            }
        );
        return response.ok;
    } catch (err) {
        console.warn(`[Cache Helper] Error writing to ${patternId}:`, err);
        return false;
    }
};

/**
 * Clear cache for a specific pattern (user-scoped)
 * 
 * @param {string} patternId - Pattern identifier to clear
 * @returns {Promise<boolean>} - True if cleared successfully
 */
export const clearPatternCache = async (patternId) => {
    try {
        const response = await fetch(
            `${API_BASE_URL}/api/cache/pattern-clear`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    pattern_id: patternId,
                })
            }
        );

        return response.ok;
    } catch (err) {
        console.warn(`[Cache Helper] Error clearing ${patternId}:`, err);
        return false;
    }
};

/**
 * Clear all pattern caches for current user
 * Useful when user logs out or switches accounts
 * 
 * @returns {Promise<boolean>} - True if cleared successfully
 */
export const clearAllPatternCaches = async () => {
    try {
        const response = await fetch(
            `${API_BASE_URL}/api/cache/pattern-clear-all`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            }
        );

        return response.ok;
    } catch (err) {
        console.warn('[Cache Helper] Error clearing all caches:', err);
        return false;
    }
};

/**
 * Get cache TTL remaining in seconds (for debugging)
 * 
 * @param {string} patternId - Pattern identifier to check
 * @returns {Promise<number>} - Seconds remaining (0 if expired or not found)
 */
export const getCacheTTLRemaining = async (patternId) => {
    try {
        const response = await fetch(
            `${API_BASE_URL}/api/cache/pattern-ttl?pattern_id=${encodeURIComponent(patternId)}`,
            {
                credentials: 'include',
            }
        );

        if (!response.ok) {
            return 0;
        }

        const result = await response.json();
        return result.ttl_remaining_seconds || 0;
    } catch (err) {
        return 0;
    }
};